import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useConfigContext from '@/data/hook/config';
import useHiddenComponents from '@/data/hook/useHiddenComponent';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useListService from '@/data/hook/useListService';
import { scheduledLessonsQueryKeys } from '@/data/services/querykeys';
import { ScheduledLessonFilters } from '@/data/services/scheduledLessonsServices';
import { isTeacher } from '@/functions/auth';
import { FilterProps } from '@/models/FilterConfig';
import User from '@/models/User';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { Fields, searchParamsToObject } from '@/utils/searchParamsToObject';
import { EyeSlashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import ManageScheduledLesson from '../admin/calendar/ManageScheduledLesson/ManageScheduledLesson';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import HiddenWrapper, {
  HiddenOption,
  HiddenWrapperProps,
} from '@/components/ui/custom/HiddenWrapper/HiddenWrapper';
import InfinityList from '@/components/ui/custom/InfinityList';
import Filter from '../filter/Filter';
import TextArrowOrderingFilter from '../filter/TextArrowOrderingFilter';
import { LoadingIcon } from '../icons';
import { CollapsableContainer } from '../teacher/CollapsableCard';
import { twMerge } from 'tailwind-merge';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { KlassStatusEnum } from '@/models/Klass';

type Props = Pick<HiddenWrapperProps, 'setRef' | 'className'> & {
  user?: User;
};

export default function YourLessons(props: Props) {
  const { user, className } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { components } = useHiddenComponents();

  const component = components['yourLessons'];

  const configFields: Fields<ScheduledLessonFilters> = {
    teacher: { type: 'number' },
    isPendingReport: { type: 'string' },
    date: { type: 'string' },
    hasDone: { type: 'string' },
    ordering: { type: 'string' },
  };

  const initialValues: Partial<ScheduledLessonFilters> = {
    teacher: isTeacher(user?.userType) ? user?.id : undefined,
    unit: getAuthorizedUnits(user),
    isActive: true,
    hasDone: false,
    ordering: '-datetime',
    dateRangeBefore: moment().format('YYYY-MM-DD'),
    klassStatus: [KlassStatusEnum.IN_PROGRESS, KlassStatusEnum.OPEN],
  };

  const noUrlFilter = searchParamsToObject<ScheduledLessonFilters>(
    initialValues,
    configFields,
  );

  const [filter, setFilter] = useState(noUrlFilter);

  const onChangeFilter = (filters: ScheduledLessonFilters) => {
    const changes = buildChangedObject(filter, filters);
    if (changes.date) setFilter({ ...filters, isPendingReport: undefined });
    else setFilter({ ...filters, date: undefined });
  };

  const { hideComponent, hiddenComponents } = useConfigContext();

  const render = hiddenComponents?.yourLessons;

  const options: Array<HiddenOption> = [
    {
      icon: <EyeSlashIcon className="w-4" />,
      title: t('hideSection'),
      onClick: () => hideComponent({ yourLessons: false }),
    },
  ];

  const { queryKey: scheduledLessonsQuerykey, queryFn: listScheduledLessons } =
    scheduledLessonsQueryKeys.list({
      isPendingReport: true,
      teacher: user?.id,
    });

  const { meta, invalidate: invalidatePast } = useListService({
    queryKey: scheduledLessonsQuerykey,
    queryFn: listScheduledLessons,
    enabled: !!user,
  });

  const {
    results: scheduledLessons,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    invalidate: invalidateScheduledLessons,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      ...filter,
      pageSize: 7,
    })._ctx.infinity,
    staleTime,
    retry: false,
  });

  const invalidate = async () =>
    await Promise.all([invalidateScheduledLessons(), invalidatePast()]);

  const configs: FilterProps[] = [
    {
      type: 'button',
      label: t('today'),
      name: 'date',
      value: moment().format('YYYY-MM-DD'),
    },
    {
      type: 'button',
      label: t('pending'),
      name: 'isPendingReport',
      badge: meta?.pagination.count,
    },
  ];

  return (
    <HiddenWrapper
      {...props}
      render={render}
      className={{ base: twMerge('overflow-hidden', className?.base) }}
    >
      <HiddenWrapper.Header
        title={component.title}
        icon={component.icon}
        header={
          <Tooltip text={t('refresh')}>
            <IconButton
              icon={<ArrowPathIcon className="h-6 text-accent" />}
              onClick={invalidate}
            />
          </Tooltip>
        }
      >
        <HiddenWrapper.Options options={options} />
      </HiddenWrapper.Header>
      <HiddenWrapper.Section className={{ section: 'h-full' }}>
        <CollapsableContainer
          isCollapsable
          className={{ footer: 'w-full' }}
          listViewSize={205}
        >
          {ref => (
            <div className="overflow-hidden h-full pb-4">
              <aside className="flex justify-between px-4 pt-1">
                <Filter
                  filter={filter}
                  configs={configs}
                  onChange={onChangeFilter}
                  hideQuick
                />

                <TextArrowOrderingFilter
                  name="ordering"
                  hideClear
                  value="-datetime"
                  options={[
                    {
                      label: t('newest'),
                      value: '-datetime',
                    },
                    {
                      label: t('oldest'),
                      value: 'datetime',
                    },
                  ]}
                  type="textArrowOrdering"
                  setValue={(name, value) =>
                    setFilter({ ...filter, [name]: value })
                  }
                />
              </aside>
              <InfinityList
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onReachEnd={fetchNextPage}
                className="gap-2 scrollbar-none grow min-h-[168px] max-h-96 p-4"
                ref={ref}
                scroll
              >
                <ConditionalRenderer condition={isInitialLoading}>
                  <div className="flex w-full h-full justify-center">
                    <LoadingIcon className="text-primary/40 w-10" />
                  </div>
                </ConditionalRenderer>

                <ConditionalRenderer condition={error}>
                  <div className="flex w-full h-full justify-center">
                    <Text format="rubik-400" text={getErrorMessage(error)} />
                  </div>
                </ConditionalRenderer>

                {scheduledLessons.map(scheduledLesson => (
                  <ManageScheduledLesson
                    state="view"
                    key={scheduledLesson.id}
                    scheduledLesson={scheduledLesson}
                  />
                ))}

                <ConditionalRenderer
                  condition={
                    !scheduledLessons.length && !isInitialLoading && !error
                  }
                >
                  <div className="flex w-full h-full justify-center pt-4">
                    <Text
                      text={`${t('noResults')} ¯\\_(ツ)_/¯`}
                      className="overflow-hidden self-center text-primary"
                    />
                  </div>
                </ConditionalRenderer>
              </InfinityList>
            </div>
          )}
        </CollapsableContainer>
      </HiddenWrapper.Section>
    </HiddenWrapper>
  );
}
